import { z } from 'zod';
import { platformApprovedActionEvidenceSchema } from '@crest/platform-contracts/hosts';
const uuid = z.string().uuid();
const hash = z.string().regex(/^[a-f0-9]{64}$/);
const identifier = z.string().min(3).max(160)
    .regex(/^[a-z][a-z0-9-]*(?:\.[a-z0-9-]+)+$/);
const versionedIdentifier = z.string().min(5).max(160)
    .regex(/^[a-z][a-z0-9-]*(?:\.[a-z0-9-]+)+\.v[1-9][0-9]*$/);
export const assistantActionExecutionCapability = 'assistant.action-execution.v1';
export const assistantActionExecutionStatusSchema = z.enum([
    'queued', 'running', 'completed', 'unavailable', 'failed', 'cancelled',
]);
export const assistantActionExecutionFailureCodeSchema = z.enum([
    'proposal-not-approved', 'proposal-expired', 'proposal-changed',
    'provider-not-configured', 'provider-not-enabled', 'provider-permission-denied',
    'provider-contract-changed', 'provider-invalid-response',
    'input-invalid', 'target-unavailable', 'target-changed',
    'permission-denied', 'action-unavailable', 'action-failed',
    'cancelled-by-user',
]);
export const assistantActionExecutionFailureSchema = z.strictObject({
    code: assistantActionExecutionFailureCodeSchema,
    message: z.string().trim().min(1).max(500),
    retryable: z.boolean(),
});
export const assistantActionExecutionSchema = z.strictObject({
    id: uuid,
    tenantId: uuid,
    proposalId: uuid,
    proposalRevision: z.number().int().positive(),
    providerId: identifier,
    providerModuleId: z.string().min(2).max(64).regex(/^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/),
    providerCapability: versionedIdentifier,
    actionId: versionedIdentifier,
    targetResourceType: identifier,
    targetResourceId: uuid.nullable(),
    inputHash: hash,
    inputSchemaHash: hash,
    status: assistantActionExecutionStatusSchema,
    attempt: z.number().int().positive().max(20),
    revision: z.number().int().positive(),
    proposalRequestedByMembershipId: uuid,
    approvedByMembershipId: uuid,
    executionRequestedByMembershipId: uuid,
    runAsMembershipId: uuid,
    result: platformApprovedActionEvidenceSchema.nullable(),
    resultHash: hash.nullable(),
    failure: assistantActionExecutionFailureSchema.nullable(),
    createdAt: z.string().datetime({ offset: true }),
    startedAt: z.string().datetime({ offset: true }).nullable(),
    completedAt: z.string().datetime({ offset: true }).nullable(),
    cancelledAt: z.string().datetime({ offset: true }).nullable(),
}).superRefine((execution, context) => {
    const completed = execution.status === 'completed';
    const failed = ['unavailable', 'failed', 'cancelled'].includes(execution.status);
    if (completed !== (execution.result !== null) || completed === (execution.resultHash === null)) {
        context.addIssue({ code: 'custom', path: ['result'], message: 'Completed execution evidence is inconsistent.' });
    }
    if (failed !== (execution.failure !== null)) {
        context.addIssue({ code: 'custom', path: ['failure'], message: 'Execution failure evidence is inconsistent.' });
    }
    if ((completed || failed) !== (execution.completedAt !== null)) {
        context.addIssue({ code: 'custom', path: ['completedAt'], message: 'Execution terminal time is inconsistent.' });
    }
    if ((execution.status === 'cancelled') !== (execution.cancelledAt !== null)) {
        context.addIssue({ code: 'custom', path: ['cancelledAt'], message: 'Execution cancellation time is inconsistent.' });
    }
});
export const assistantActionExecutionEventSchema = z.strictObject({
    id: uuid,
    tenantId: uuid,
    executionId: uuid,
    sequence: z.number().int().positive(),
    eventType: z.enum([
        'queued', 'running', 'completed', 'unavailable', 'failed', 'cancelled', 'retried',
    ]),
    status: assistantActionExecutionStatusSchema,
    failureCode: assistantActionExecutionFailureCodeSchema.nullable(),
    createdAt: z.string().datetime({ offset: true }),
});
export const assistantActionExecutionStartSchema = z.strictObject({
    commandId: uuid,
    expectedProposalRevision: z.number().int().positive(),
});
export const assistantActionExecutionRetrySchema = z.strictObject({
    commandId: uuid,
    expectedRevision: z.number().int().positive(),
});
export const assistantActionExecutionCancelSchema = z.strictObject({
    commandId: uuid,
    expectedRevision: z.number().int().positive(),
    reason: z.string().trim().min(10).max(500),
});
export const assistantActionExecutionMutationResultSchema = z.strictObject({
    execution: assistantActionExecutionSchema,
    idempotent: z.boolean(),
});
export const assistantActionExecutionDetailsSchema = z.strictObject({
    execution: assistantActionExecutionSchema,
    events: z.array(assistantActionExecutionEventSchema).max(250),
});
//# sourceMappingURL=assistant-action-execution.contract.js.map